import * as React from 'react';
import { useRouter } from 'next/router';
import { RiLayoutRowLine } from 'react-icons/ri';
import { IoArrowRedoOutline, IoChevronDown, IoSettingsSharp } from 'react-icons/io5';
import { TbCalendarDollar } from 'react-icons/tb';
import { ImTable } from 'react-icons/im';
import { PiDotsThreeOutlineVerticalFill } from 'react-icons/pi';
import Link from 'components/Link';
import { BsLightning } from 'react-icons/bs';
import { XIcon } from '@heroicons/react/solid';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import { FaDownload, FaLock } from 'react-icons/fa';
import { trackEvent } from 'services/client/analytics';
import { DOWNLOAD_FLOW, FLOW_CHAIN_VIEW_OPENED } from 'services/client/analytics/events';
import { Switch, Popover } from '@headlessui/react';
import { BsArrowsExpand, BsArrowsCollapse } from 'react-icons/bs';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import { getSectorShortName } from 'utils/shared/trades/sectors';
import { TICKER_FLOW_PAGE, PRICING_PAGE } from 'constants/pages';
import { ENABLE_OPTION_FLOW_READ_MODE } from 'utils/shared/trades/optionFlowRead';
import SectionTitle from 'components/SectionTitle';
import { HeatScore } from 'components/Score';
import SpeakerButton from 'components/SpeakerButton';
import MobileOptionCard, { MOBILE_CARD_PIXEL_HEIGHT } from 'components/MobileOptionCard';
import { SortDirection } from 'utils/shared/trades/types';
import { convertToCSV, downloadCSV } from 'utils/client/csvUtils';
import {
  TableWrapper,
  TableCell,
  TableCellSmall,
  TableCellNoPad,
  TopPositionText,
  TableHeader,
  TableBody,
  HeaderCell,
  CallPutBackground,
  CallPutText,
  MutedText,
  HeaderCellWrapper,
  HeaderIcon,
  IconSortDesc,
  IconSortAsc,
  WatchListIcon,
  StyledTableBody,
} from 'components/Table';
import Modal from 'components/Modal/HeadlessUiModal';
import SubscribeModal from 'components/SubscribeModal';
import {
  FlowTableHeaderProps,
  FlowTableRowProps,
  FlowTableProps,
  MobileLayout,
  FullTrade,
} from 'utils/shared/trades/types';
import { getEarningsTooltip } from 'utils/shared/earnings';
import classNames from 'styles/utils/classNames';
import { FLOW_TABLE_HEADERS, DEFAULT_FLOW_KEY } from './props';
import { LOGIN_PAGE, SIGNUP_PAGE } from 'constants/pages';
import { useEarningsData } from 'hooks/useEarningsQuery';
import { useWatchlistEarningsFilter } from 'hooks/useWatchlistEarningsFilter';
import TooltipComponent from 'components/Tooltip';
import MoreFeatureModal from 'components/OptionFlowTable/MoreFeatureModal';
import OptionFlowTradeSide from 'components/OptionFlowTradeSide/OptionFlowTradeSide';
import OptionFlowTradeRead from 'components/OptionFlowTradeSide/OptionFlowTradeRead';
import OptionChainTable, {
  CHAIN_TABLE_HEADERS_MULTI_NO_DATE,
  ChainSortState,
} from 'components/OptionChainTable';
import ChainTableHeader from 'components/OptionChainTable/ChainTableHeader';
import { PARENT_COLUMNS_MULTI_NO_DATE } from 'components/OptionChainTable/styles';
import groupTradesIntoChain from 'utils/shared/trades/groupTradesIntoChain';
import { getOptionFlowRowSentiment } from 'utils/shared/trades/getOptionFlowRowSentiment';
import { formatOptionFlowCsvData } from 'utils/shared/trades/formatOptionFlowCsvData';
import { getNextOptionFlowSortState } from 'utils/shared/trades/sortOptionFlowTrades';
import DelayedFlowNotice from 'components/DelayedFlowNotice';
import FlowOptionGraphView from './FlowOptionGraphView';

import { DashboardRow, IconButton, ViewToggle } from './styles';

const TABLE_ROW_PIXEL_HEIGHT = 36.2;
const TITLE_NAMES = FLOW_TABLE_HEADERS;

const handleDownload = (data: FullTrade[]): void => {
  const formattedData = formatOptionFlowCsvData(data);
  const csv = convertToCSV(formattedData);
  downloadCSV(csv, 'Realtime Option Flow');
};

const NeedsSubscription = ({ hiddenTrades }: { hiddenTrades?: number }) => {
  const tradesToConsider = Math.max(hiddenTrades || 0, 0);

  return (
    <div className="text-pretty bg-brand-primary px-2 py-1.5 text-center text-xs font-medium text-white md:px-0">
      <Link href={PRICING_PAGE}>
        {!!tradesToConsider && (
          <span className="mr-1 rounded-md bg-yellow-400 px-1.5 py-[.1rem] text-xs font-bold leading-none text-black no-underline shadow-md">
            {tradesToConsider} hidden trades
          </span>
        )}
        <span className="font-bold underline">Subscribe</span>
      </Link>{' '}
      to access the full feed. Trades are delayed by 30 minutes, and you are limited to the most
      recent.
    </div>
  );
};

const SoundSettingsModal = ({
  isOpen,
  handleClose,
  setIsSoundEnabled,
}: {
  isOpen: boolean;
  handleClose: () => void;
  setIsSoundEnabled: (value: boolean) => void;
}) => {
  const router = useRouter();
  const currentPath = router.asPath;

  return (
    <Modal isOpen={isOpen} handleClose={() => handleClose()}>
      <div className="relative rounded-lg bg-palette-black-1 p-4">
        <button onClick={() => handleClose()} className="absolute right-2 top-2">
          <XIcon className="w-5" />
        </button>
        <div className="flex justify-center">
          <FaLock className="mr-2" />
        </div>
        <div className="text-center text-lg font-semibold">Saving flow sounds locked</div>
        <>
          <div className="text-pretty text-center text-lg font-normal text-brand-green-primary">
            Create a free account or
            <Link href={`${LOGIN_PAGE}?next=${currentPath}`} className="px-1 underline">
              login
            </Link>
            to save your sound settings.
          </div>
          <div className="flex justify-center gap-2 py-4">
            <Link
              role="button"
              href={`${SIGNUP_PAGE}?next=${currentPath}`}
              className="flex items-center justify-center rounded-md border border-transparent bg-brand-primary px-8 py-2 text-base font-medium text-white no-underline"
            >
              <span className="mr-2">Get Free Access</span>
              <IoArrowRedoOutline />
            </Link>
          </div>
          <div
            className="cursor-pointer text-center text-xs text-palette-black-8 underline"
            onClick={() => {
              setIsSoundEnabled(false);
              handleClose();
            }}
          >
            Mute only for this session
          </div>
        </>
      </div>
    </Modal>
  );
};
export enum ViewType {
  Flow = 'FLOW',
  Chain = 'CHAIN',
  Graph = 'GRAPH',
}

// Mobile chain card height constants (adjust if card styling changes)
export const Header: React.FC<
  React.PropsWithChildren<
    FlowTableHeaderProps & {
      viewType: ViewType;
      setViewType: (v: ViewType) => void;
      onExpandAll?: () => void;
      onCollapseAll?: () => void;
      allChainExpanded?: boolean;
      hasChainRows?: boolean;
      chainSortState?: ChainSortState | null;
    }
  >
> = React.memo(
  ({
    flowSortKey,
    setFlowSortKey,
    flowSortDirection,
    setFlowSortDirection,
    isWatchListOnly,
    setIsWatchListOnly,
    hasWatchListItems,
    isSoundEnabled,
    setIsSoundEnabled,
    mobileLayout,
    setMobileLayout,
    data,
    hasAccess,
    isUserSession,
    setIsWatchListModalOpen,
    isEarningsOnly,
    setIsEarningsOnly,
    isShowcase,
    viewType,
    setViewType,
    onExpandAll,
    onCollapseAll,
    allChainExpanded,
    hasChainRows,
    chainSortState,
    metricMode = 'total',
  }) => {
    const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
    const [isSoundModalOpen, setIsSoundModalOpen] = React.useState(false);
    return (
      <>
        <TableHeader>
          {/* Desktop: single row */}
          <div className="mb-2 hidden items-center justify-between sm:flex">
            <div className="flex items-center">
              <SectionTitle
                Icon={BsLightning}
                title="Realtime Option Flow"
                style={{ marginBottom: 0 }}
              />
              <div className="ml-4 flex items-center">
                <ViewToggle
                  isActive={viewType === ViewType.Flow}
                  onClick={() => setViewType(ViewType.Flow)}
                >
                  FLOW
                </ViewToggle>
                <ViewToggle
                  isActive={viewType === ViewType.Chain}
                  onClick={() => setViewType(ViewType.Chain)}
                >
                  CHAIN
                </ViewToggle>
                <ViewToggle
                  isActive={viewType === ViewType.Graph}
                  onClick={() => setViewType(ViewType.Graph)}
                >
                  GRAPH
                </ViewToggle>
              </div>
              {viewType === ViewType.Chain && hasChainRows && (
                <TooltipComponent
                  title={allChainExpanded ? 'Collapse all chains' : 'Expand all chains'}
                  position="bottom"
                >
                  <button
                    className="ml-3 flex items-center gap-1 rounded border border-gray-600 px-2 py-0.5 text-[0.6rem] text-gray-400 hover:border-gray-400 hover:text-gray-200"
                    onClick={allChainExpanded ? onCollapseAll : onExpandAll}
                  >
                    {allChainExpanded ? (
                      <BsArrowsCollapse className="text-xs" />
                    ) : (
                      <BsArrowsExpand className="text-xs" />
                    )}
                  </button>
                </TooltipComponent>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <SpeakerButton
                className="shadow-sm"
                isSoundEnabled={isSoundEnabled}
                setIsSoundEnabled={(enabled) => {
                  if (!isUserSession) {
                    setIsSoundModalOpen(true);
                    return;
                  }
                  setIsSoundEnabled(enabled);
                }}
              />
              <Switch.Group as="div" className="flex items-center">
                <Switch
                  checked={isWatchListOnly}
                  onChange={setIsWatchListOnly}
                  className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                >
                  <span
                    aria-hidden="true"
                    className={`${isWatchListOnly ? 'bg-yellow-400' : 'bg-gray-600'}
                    pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out
                  `}
                  />
                  <span
                    aria-hidden="true"
                    className={`${isWatchListOnly ? 'translate-x-3' : 'translate-x-0'} ${
                      isWatchListOnly ? 'bg-white' : 'bg-gray-300'
                    } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                  />
                </Switch>
                <div className="ml-2 text-xs">
                  <TooltipComponent title="Update your watchlist" position="bottom">
                    <button
                      type="button"
                      onClick={() => setIsWatchListModalOpen?.(true)}
                      className="flex cursor-pointer items-center whitespace-nowrap rounded-[4px] border border-gray-300 bg-gray-50 px-1 py-1 text-xs font-semibold text-yellow-500 shadow-sm transition-colors hover:bg-gray-100 active:bg-gray-200"
                    >
                      <AiFillStar
                        aria-hidden="true"
                        className={isWatchListOnly ? 'text-yellow-400' : 'text-gray-600'}
                      />
                    </button>
                  </TooltipComponent>
                </div>
                {isWatchListOnly && !hasWatchListItems && (
                  <Switch.Label as="span" className="ml-2 leading-none" style={{ lineHeight: 0 }}>
                    <span className="text-xs font-medium leading-none text-yellow-400">
                      Add items to your watchlist to appear in the feed
                    </span>
                  </Switch.Label>
                )}
              </Switch.Group>
              <Switch.Group as="div" className="flex items-center">
                <Switch
                  checked={isEarningsOnly || false}
                  onChange={setIsEarningsOnly || (() => {})}
                  className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                >
                  <span
                    aria-hidden="true"
                    className={`${isEarningsOnly ? 'bg-graph-purple' : 'bg-gray-600'}
                                  pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out
                                `}
                  />
                  <span
                    aria-hidden="true"
                    className={`${isEarningsOnly ? 'translate-x-3' : 'translate-x-0'} ${
                      isEarningsOnly ? 'bg-white' : 'bg-gray-300'
                    } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                  />
                </Switch>
                <div
                  className={`ml-0.5 text-xs ${
                    isEarningsOnly ? 'text-yellow-400' : 'text-gray-600'
                  }`}
                >
                  <div
                    className={`rounded-[4px] px-1 py-1 transition-all duration-300 ${
                      isEarningsOnly ? 'text-graph-purple shadow-lg' : 'text-slate-400'
                    }`}
                  >
                    <TooltipComponent title="Upcoming earnings" position="bottom">
                      <TbCalendarDollar aria-hidden="true" className="h-5 w-5" />
                    </TooltipComponent>
                  </div>
                </div>
              </Switch.Group>
              {!isShowcase && (
                <button
                  className="flex h-6 items-center justify-center rounded-[4px] bg-palette-border px-2 text-[.65rem] shadow-sm"
                  onClick={() => {
                    trackEvent(DOWNLOAD_FLOW, { hasAccess });
                    if (!hasAccess) {
                      return setIsDownloadModalOpen(true);
                    }
                    if (data) {
                      handleDownload(data);
                    }
                  }}
                >
                  <FaDownload />
                  <span className="ml-2 text-[.6rem] font-semibold">Download</span>
                </button>
              )}
            </div>
          </div>
          {/* Mobile: single row */}
          <div className="mb-2 flex items-center justify-between sm:hidden">
            <div className="flex items-center">
              <SectionTitle Icon={BsLightning} title="Flow" style={{ marginBottom: 0 }} />
              <div className="ml-2 flex items-center">
                <ViewToggle
                  isActive={viewType === ViewType.Flow}
                  onClick={() => setViewType(ViewType.Flow)}
                >
                  FLOW
                </ViewToggle>
                <ViewToggle
                  isActive={viewType === ViewType.Chain}
                  onClick={() => setViewType(ViewType.Chain)}
                >
                  CHAIN
                </ViewToggle>
                <ViewToggle
                  isActive={viewType === ViewType.Graph}
                  onClick={() => setViewType(ViewType.Graph)}
                >
                  GRAPH
                </ViewToggle>
              </div>
              {viewType === ViewType.Chain && hasChainRows && (
                <button
                  className="ml-2 flex items-center rounded border border-gray-600 px-1.5 py-0.5 text-gray-400 hover:border-gray-400 hover:text-gray-200"
                  onClick={allChainExpanded ? onCollapseAll : onExpandAll}
                >
                  {allChainExpanded ? (
                    <BsArrowsCollapse className="text-xs" />
                  ) : (
                    <BsArrowsExpand className="text-xs" />
                  )}
                </button>
              )}
            </div>
            <Popover className="relative">
              <Popover.Button className="flex items-center gap-1 rounded bg-brand-primary px-2 py-1.5 text-white hover:bg-brand-primary/80 focus:outline-none">
                <IoSettingsSharp className="h-3.5 w-3.5" />
                <IoChevronDown className="h-2.5 w-2.5" />
              </Popover.Button>
              <Popover.Panel className="absolute right-0 z-50 mt-1 w-48 rounded-lg border border-gray-700 bg-palette-black-1 p-2 shadow-lg">
                <div className="flex flex-col space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">Watchlist</span>
                    <Switch.Group as="div" className="flex items-center">
                      <Switch
                        checked={isWatchListOnly}
                        onChange={setIsWatchListOnly}
                        className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                      >
                        <span
                          aria-hidden="true"
                          className={`${isWatchListOnly ? 'bg-yellow-400' : 'bg-gray-600'}
                          pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out
                        `}
                        />
                        <span
                          aria-hidden="true"
                          className={`${isWatchListOnly ? 'translate-x-3' : 'translate-x-0'} ${
                            isWatchListOnly ? 'bg-white' : 'bg-gray-300'
                          } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                        />
                      </Switch>
                      <button
                        type="button"
                        onClick={() => setIsWatchListModalOpen?.(true)}
                        className="ml-1 flex cursor-pointer items-center"
                      >
                        <AiFillStar
                          aria-hidden="true"
                          className={`h-3.5 w-3.5 ${
                            isWatchListOnly ? 'text-yellow-400' : 'text-gray-600'
                          }`}
                        />
                      </button>
                    </Switch.Group>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">Earnings</span>
                    <Switch.Group as="div" className="flex items-center">
                      <Switch
                        checked={isEarningsOnly || false}
                        onChange={setIsEarningsOnly || (() => {})}
                        className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                      >
                        <span
                          aria-hidden="true"
                          className={`${isEarningsOnly ? 'bg-graph-purple' : 'bg-gray-600'}
                          pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out
                        `}
                        />
                        <span
                          aria-hidden="true"
                          className={`${isEarningsOnly ? 'translate-x-3' : 'translate-x-0'} ${
                            isEarningsOnly ? 'bg-white' : 'bg-gray-300'
                          } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                        />
                      </Switch>
                      <div
                        className={`ml-0.5 ${
                          isEarningsOnly ? 'text-graph-purple' : 'text-slate-400'
                        }`}
                      >
                        <TbCalendarDollar aria-hidden="true" className="h-4 w-4" />
                      </div>
                    </Switch.Group>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">Layout</span>
                    <div className="flex items-center space-x-1">
                      <IconButton
                        type="button"
                        isActive={mobileLayout === MobileLayout.Cards}
                        onClick={() => !!setMobileLayout && setMobileLayout(MobileLayout.Cards)}
                      >
                        <RiLayoutRowLine />
                      </IconButton>
                      <IconButton
                        type="button"
                        isActive={mobileLayout === MobileLayout.Table}
                        onClick={() => !!setMobileLayout && setMobileLayout(MobileLayout.Table)}
                      >
                        <ImTable />
                      </IconButton>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">Sound</span>
                    <SpeakerButton
                      className="shadow-sm"
                      isSoundEnabled={isSoundEnabled}
                      setIsSoundEnabled={(enabled) => {
                        if (!isUserSession) {
                          setIsSoundModalOpen(true);
                          return;
                        }
                        setIsSoundEnabled(enabled);
                      }}
                    />
                  </div>
                  {!isShowcase && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Download</span>
                      <button
                        className="flex h-6 items-center justify-center rounded-[4px] bg-palette-border px-2 text-[.65rem] shadow-sm"
                        onClick={() => {
                          trackEvent(DOWNLOAD_FLOW, { hasAccess });
                          if (!hasAccess) {
                            return setIsDownloadModalOpen(true);
                          }
                          if (data) {
                            handleDownload(data);
                          }
                        }}
                      >
                        <FaDownload />
                      </button>
                    </div>
                  )}
                </div>
              </Popover.Panel>
            </Popover>
          </div>
          {viewType === ViewType.Flow && (
            <DashboardRow removePad className="h-10">
              {TITLE_NAMES.map((t, i) => {
                const isActiveSort =
                  t.sortKey === flowSortKey && flowSortDirection !== SortDirection.None;
                return (
                  <HeaderCellWrapper
                    key={t.name}
                    removePad={i + 1 === TITLE_NAMES.length}
                    isActiveSort={isActiveSort}
                    onClick={() => {
                      const nextSortState = getNextOptionFlowSortState({
                        currentSortKey: flowSortKey,
                        currentSortDirection: flowSortDirection,
                        selectedSortKey: t.sortKey,
                        defaultSortKey: DEFAULT_FLOW_KEY,
                      });

                      setFlowSortKey(nextSortState.sortKey);
                      setFlowSortDirection(nextSortState.sortDirection);
                    }}
                  >
                    <HeaderCell>
                      <span className="cell-name">{t.name}</span>
                      {isActiveSort && flowSortDirection === SortDirection.Desc && (
                        <HeaderIcon>
                          <IconSortDesc />
                        </HeaderIcon>
                      )}
                      {isActiveSort && flowSortDirection === SortDirection.Asc && (
                        <HeaderIcon>
                          <IconSortAsc />
                        </HeaderIcon>
                      )}
                    </HeaderCell>
                  </HeaderCellWrapper>
                );
              })}
            </DashboardRow>
          )}
          {viewType === ViewType.Chain && chainSortState && (
            <ChainTableHeader
              headers={CHAIN_TABLE_HEADERS_MULTI_NO_DATE}
              columns={PARENT_COLUMNS_MULTI_NO_DATE}
              chainSortState={chainSortState}
              metricMode={metricMode}
            />
          )}
        </TableHeader>
        <SubscribeModal
          isOpen={isDownloadModalOpen}
          onClose={() => setIsDownloadModalOpen(false)}
        />
        <SoundSettingsModal
          isOpen={isSoundModalOpen}
          handleClose={() => setIsSoundModalOpen(false)}
          setIsSoundEnabled={setIsSoundEnabled}
        />
      </>
    );
  },
);

const Row: React.FC<
  React.PropsWithChildren<
    FlowTableRowProps & {
      onOpenDrawer?: (trade: FullTrade) => void;
      onOpenPopover?: (trade: FullTrade, anchorElement?: HTMLElement) => void;
    }
  >
> = ({
  trade,
  watchListTickers,
  trackRowAsAnimated,
  hasBeenAnimated,
  handleBookmark,
  earningsMap,
  onOpenDrawer,
  onOpenPopover,
}) => {
  const sentiment = getOptionFlowRowSentiment(trade);
  const isWatchListTrade = !!trade.ticker && watchListTickers.includes(trade.ticker);
  const { hasEarnings, getDaysToEarnings, getEarningsData } = useEarningsData(
    earningsMap?.earnings,
  );

  const earningsData = getEarningsData(trade?.ticker || '');
  const daysToEarnings = getDaysToEarnings(trade?.ticker || '');
  const tickerHasEarnings = hasEarnings(trade?.ticker || '');

  const onBookmarkClick = () => {
    if (handleBookmark && trade.ticker) {
      handleBookmark(trade.ticker);
    }
  };

  React.useEffect(() => {
    return () => {
      if (trackRowAsAnimated && !hasBeenAnimated) {
        trackRowAsAnimated();
      }
    };
  }, [hasBeenAnimated, trackRowAsAnimated]);

  return (
    <div className="relative">
      {trade.isBlur && (
        <div className="absolute bottom-0 top-0 z-10 my-auto flex w-full items-center justify-center gap-1 px-4 py-[0.4rem] text-center text-[.625rem] text-palette-black-7">
          <Link href={PRICING_PAGE} className="!text-palette-black-7 underline">
            Subscribe
          </Link>{' '}
          to see the full feed
        </div>
      )}
      <DashboardRow
        tableBodyRow
        isAnimated={!hasBeenAnimated}
        isUnusual={trade.isUnusual}
        isGoldenSweep={trade.isGoldenSweep}
        className={trade.isBlur ? 'opacity-50 blur-sm' : ''}
      >
        <TableCell>
          <MutedText>
            {trade.time} {trade.timeSubscript}
          </MutedText>
        </TableCell>
        <TableCell className="relative block">
          <WatchListIcon className="absolute z-[1000] text-yellow-400">
            <button
              className={`-m-1 flex-shrink-0 cursor-pointer rounded p-1 transition-all duration-200 hover:scale-[1.75] active:scale-110 ${
                isWatchListTrade
                  ? 'text-yellow-400 hover:text-yellow-300'
                  : 'text-gray-400 hover:text-yellow-400'
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onBookmarkClick();
              }}
            >
              {isWatchListTrade ? <AiFillStar /> : <AiOutlineStar />}
            </button>
          </WatchListIcon>
          <span
            className="z-100"
            onPointerEnter={(e) => {
              if (e.pointerType === 'mouse' && onOpenPopover) {
                onOpenPopover(trade, e.currentTarget as HTMLElement);
              }
            }}
            onPointerDown={(e) => {
              if (e.pointerType === 'touch' && onOpenPopover) {
                e.preventDefault();
                onOpenPopover(trade, e.currentTarget as HTMLElement);
              }
            }}
          >
            <Link href={`${TICKER_FLOW_PAGE}/${trade.ticker}`}>
              <CallPutBackground type={trade.cp || ''}>{trade.ticker}</CallPutBackground>
            </Link>
          </span>

          {tickerHasEarnings && (
            <TooltipComponent
              title={getEarningsTooltip(earningsData, daysToEarnings)}
              position="right"
            >
              <sup className="pointer-events-auto absolute -right-2 -top-1.5 cursor-pointer">
                <TbCalendarDollar
                  aria-hidden="true"
                  className="h-3 w-3 rounded-sm bg-palette-black-2 text-graph-purple"
                />
              </sup>
            </TooltipComponent>
          )}
        </TableCell>
        <TableCell>
          <CallPutText type={trade.cp || ''}>{trade.cp}</CallPutText>
        </TableCell>
        <TableCell>
          <OptionFlowTradeSide trade={trade} />
        </TableCell>
        {ENABLE_OPTION_FLOW_READ_MODE && (
          <TableCell className="items-center">
            <OptionFlowTradeRead trade={trade} />
          </TableCell>
        )}
        <TableCell>{trade.expiryString}</TableCell>
        <TableCell>${trade.spot.toFixed(2)}</TableCell>
        <TableCell>${trade.strike}</TableCell>
        <TableCell>{Math.round(trade.otmPercent)}%</TableCell>
        <TableCell>${trade.price.toFixed(2)}</TableCell>
        <TableCell>
          <TopPositionText isTopPosition={trade.isTopPosition}>
            {trade.size?.toLocaleString()}
          </TopPositionText>
        </TableCell>
        <TableCell>{trade.openInterest?.toLocaleString()}</TableCell>
        <TableCell>{Math.round(trade.impliedVol * 100)}%</TableCell>
        <TableCellSmall>{trade.orderType}</TableCellSmall>
        <TableCell>
          <CallPutText type={sentiment}>${abbreviateNumber(trade.premium)}</CallPutText>
        </TableCell>
        <TableCell>{getSectorShortName(trade?.tickerDetails?.sector || '')}</TableCell>
        <TableCellNoPad>
          <HeatScore scoreWidth={trade.scoreWidth} />
        </TableCellNoPad>
        <TableCell className="justify-end !pr-0">
          <button
            className="active:bg-black-palette-2 flex items-center justify-center rounded transition-colors hover:text-gray-600"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenDrawer?.(trade);
            }}
            aria-label="Trade options"
          >
            <PiDotsThreeOutlineVerticalFill className="" />
          </button>
        </TableCell>
      </DashboardRow>
    </div>
  );
};

const OptionFlowTable: React.FC<React.PropsWithChildren<FlowTableProps>> = ({
  trades,
  referenceTrades,
  flowSortKey,
  setFlowSortKey,
  flowSortDirection,
  setFlowSortDirection,
  isWatchListOnly,
  setIsWatchListOnly,
  hasWatchListItems,
  isSoundEnabled,
  setIsSoundEnabled,
  watchListTickers,
  totalFlowCount,
  hasAccess,
  isUserSession,
  setIsWatchListModalOpen,
  handleBookmark,
  earningsMap,
  tradesByTicker,
  isShowcase,
  defaultMobileLayout = MobileLayout.Cards,
  onOpenPopover,
  isMobile = false,
  flowSideBasis,
  flowSideLabel,
  metricMode = 'total',
  delayedFlowWait = null,
}) => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
  const [hasLoadedTrades, setHasLoadedTrades] = React.useState(false);
  const animatedRowsRef = React.useRef<Record<string, boolean>>({});
  const [mobileLayout, setMobileLayout] = React.useState(defaultMobileLayout);
  const [isEarningsOnly, setIsEarningsOnly] = React.useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [selectedTrade, setSelectedTrade] = React.useState<FullTrade | null>(null);
  const [viewType, setViewType] = React.useState<ViewType>(ViewType.Flow);
  const [chainSortState, setChainSortState] = React.useState<ChainSortState | null>(null);
  const [expandAllTrigger, setExpandAllTrigger] = React.useState(0);
  const [collapseAllTrigger, setCollapseAllTrigger] = React.useState(0);
  const [chainExpandState, setChainExpandState] = React.useState({
    allExpanded: false,
    hasRows: false,
  });

  const handleChainExpandStateChange = React.useCallback(
    (allExpanded: boolean, hasRows: boolean) => {
      setChainExpandState({ allExpanded, hasRows });
    },
    [],
  );

  const handleExpandAll = React.useCallback(() => {
    setExpandAllTrigger((c) => c + 1);
  }, []);

  const handleCollapseAll = React.useCallback(() => {
    setCollapseAllTrigger((c) => c + 1);
  }, []);

  const { filterTickers } = useWatchlistEarningsFilter<FullTrade & { ticker: string }>(earningsMap);

  const processedTrades = React.useMemo(() => {
    const validTrades = trades.filter(
      (trade): trade is FullTrade & { ticker: string } => !!trade.ticker && trade.ticker !== null,
    );

    return filterTickers(validTrades, {
      isWatchListOnly,
      isEarningsOnly,
      watchListTickers,
    });
  }, [trades, isWatchListOnly, isEarningsOnly, watchListTickers, filterTickers]);

  // Never cover a real row: the notice only replaces a body that would otherwise be empty.
  const delayedNoticeWait = processedTrades.length === 0 ? delayedFlowWait : null;

  const handleViewTypeChange = React.useCallback(
    (nextViewType: ViewType) => {
      if (nextViewType === ViewType.Chain && viewType !== ViewType.Chain) {
        trackEvent(FLOW_CHAIN_VIEW_OPENED, {
          surface: 'option_flow_table',
          metricMode,
          flowSideLabel,
          rowCount: processedTrades.length,
        });
      }

      setViewType(nextViewType);
    },
    [flowSideLabel, metricMode, processedTrades.length, viewType],
  );

  React.useEffect(() => {
    if (processedTrades?.length) {
      const prevState = animatedRowsRef.current;
      const newRows: Record<string, boolean> = {};

      processedTrades.forEach((trade) => {
        if (hasLoadedTrades) {
          newRows[trade.id] = prevState[trade.id] ?? false;
        } else {
          newRows[trade.id] = true;
        }
      });

      animatedRowsRef.current = newRows;

      if (!hasLoadedTrades) {
        setHasLoadedTrades(true);
      }
    }
  }, [processedTrades, hasLoadedTrades]);

  const chainRows = React.useMemo(() => {
    if (viewType !== ViewType.Chain) return [];
    if (!processedTrades || processedTrades.length === 0) return [];
    return groupTradesIntoChain(processedTrades, { includeTicker: true, metricMode });
  }, [processedTrades, viewType, metricMode]);

  const handleOpenDrawer = React.useCallback((trade: FullTrade) => {
    setSelectedTrade(trade);
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = React.useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedTrade(null);
  }, []);

  const tradesByTickerLookup = React.useMemo(() => {
    if (!tradesByTicker) return {};
    const map: Record<string, typeof tradesByTicker> = {};
    tradesByTicker.forEach((t) => {
      if (t.ticker) {
        if (!map[t.ticker]) map[t.ticker] = [];
        map[t.ticker].push(t);
      }
    });
    return map;
  }, [tradesByTicker]);

  const RenderRow = React.useCallback(
    ({ index, style }: any) => {
      const trade = processedTrades[index];
      const filteredTradesByTicker = tradesByTickerLookup[trade.ticker] || [];
      return (
        <div style={style}>
          <Row
            key={trade.id}
            trade={trade}
            watchListTickers={watchListTickers}
            hasBeenAnimated={animatedRowsRef.current[trade.id]}
            trackRowAsAnimated={() => {
              animatedRowsRef.current[trade.id] = true;
            }}
            handleBookmark={handleBookmark}
            earningsMap={earningsMap}
            tradesByTicker={filteredTradesByTicker}
            onOpenDrawer={handleOpenDrawer}
            onOpenPopover={onOpenPopover}
          />
        </div>
      );
    },
    [
      processedTrades,
      watchListTickers,
      handleBookmark,
      earningsMap,
      tradesByTickerLookup,
      handleOpenDrawer,
      onOpenPopover,
    ],
  );
  const RenderCard = React.useCallback(
    ({ index, style }: any) => {
      const trade = processedTrades[index];
      return (
        <div style={style}>
          <MobileOptionCard
            key={trade.id}
            trade={trade}
            watchListTickers={watchListTickers}
            earningsMap={earningsMap}
            onOpenPopover={onOpenPopover}
          />
        </div>
      );
    },
    [processedTrades, watchListTickers, earningsMap, onOpenPopover],
  );

  return (
    <>
      {(!isMobile || mobileLayout === MobileLayout.Table) && (
        <TableWrapper
          className={classNames(
            'h-full w-full overflow-x-auto overflow-y-hidden',
            isMobile && mobileLayout === MobileLayout.Cards ? 'hidden' : 'grid',
          )}
        >
          <Header
            flowSortKey={flowSortKey}
            setFlowSortKey={setFlowSortKey}
            flowSortDirection={flowSortDirection}
            setFlowSortDirection={setFlowSortDirection}
            isWatchListOnly={isWatchListOnly}
            setIsWatchListOnly={setIsWatchListOnly}
            hasWatchListItems={hasWatchListItems}
            isSoundEnabled={isSoundEnabled}
            setIsSoundEnabled={setIsSoundEnabled}
            mobileLayout={mobileLayout}
            setMobileLayout={setMobileLayout}
            data={processedTrades}
            hasAccess={hasAccess}
            isUserSession={isUserSession}
            setIsWatchListModalOpen={setIsWatchListModalOpen}
            isEarningsOnly={isEarningsOnly}
            setIsEarningsOnly={setIsEarningsOnly}
            isShowcase={isShowcase}
            viewType={viewType}
            setViewType={handleViewTypeChange}
            onExpandAll={handleExpandAll}
            onCollapseAll={handleCollapseAll}
            allChainExpanded={chainExpandState.allExpanded}
            hasChainRows={chainExpandState.hasRows}
            chainSortState={chainSortState}
            metricMode={metricMode}
          />
          {viewType === ViewType.Flow && (
            <TableBody animateRows className="h-full w-full">
              <div className="h-full w-full overflow-hidden">
                {!hasAccess && !isShowcase && (
                  <NeedsSubscription hiddenTrades={(totalFlowCount || 0) - trades.length} />
                )}
                {delayedNoticeWait ? (
                  <DelayedFlowNotice sessionDate={delayedNoticeWait.sessionDate} />
                ) : (
                  <AutoSizer>
                    {({ width, height }) => (
                      <FixedSizeList
                        height={height}
                        itemCount={processedTrades.length}
                        itemSize={TABLE_ROW_PIXEL_HEIGHT}
                        width={width}
                      >
                        {RenderRow}
                      </FixedSizeList>
                    )}
                  </AutoSizer>
                )}
              </div>
            </TableBody>
          )}
          {viewType === ViewType.Chain && (
            <OptionChainTable
              chainRows={chainRows}
              includeTicker
              hideDate
              headerless
              expandAllTrigger={expandAllTrigger}
              collapseAllTrigger={collapseAllTrigger}
              onExpandStateChange={handleChainExpandStateChange}
              onSortStateChange={setChainSortState}
              metricMode={metricMode}
            />
          )}
          {viewType === ViewType.Graph && (
            <FlowOptionGraphView trades={processedTrades} referenceTrades={referenceTrades} />
          )}
        </TableWrapper>
      )}
      {isMobile && (
        <div
          className={classNames(
            'h-full w-full flex-auto flex-col overflow-hidden rounded',
            mobileLayout === MobileLayout.Table ? 'hidden' : 'flex',
          )}
        >
          <TableHeader>
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center">
                <SectionTitle Icon={BsLightning} title="Flow" style={{ marginBottom: 0 }} />
                <div className="ml-2 flex items-center">
                  <ViewToggle
                    isActive={viewType === ViewType.Flow}
                    onClick={() => handleViewTypeChange(ViewType.Flow)}
                  >
                    FLOW
                  </ViewToggle>
                  <ViewToggle
                    isActive={viewType === ViewType.Chain}
                    onClick={() => handleViewTypeChange(ViewType.Chain)}
                  >
                    CHAIN
                  </ViewToggle>
                  <ViewToggle
                    isActive={viewType === ViewType.Graph}
                    onClick={() => handleViewTypeChange(ViewType.Graph)}
                  >
                    GRAPH
                  </ViewToggle>
                </div>
                {viewType === ViewType.Chain && chainExpandState.hasRows && (
                  <button
                    className="ml-2 flex items-center gap-1 rounded border border-gray-600 px-2 py-0.5 text-[0.6rem] text-gray-400 active:border-gray-400 active:text-gray-200"
                    onClick={chainExpandState.allExpanded ? handleCollapseAll : handleExpandAll}
                    aria-label={
                      chainExpandState.allExpanded ? 'Collapse all chains' : 'Expand all chains'
                    }
                  >
                    {chainExpandState.allExpanded ? (
                      <BsArrowsCollapse className="text-xs" />
                    ) : (
                      <BsArrowsExpand className="text-xs" />
                    )}
                  </button>
                )}
              </div>
              <Popover className="relative">
                <Popover.Button className="flex items-center gap-1 rounded bg-brand-primary px-2 py-1.5 text-white hover:bg-brand-primary/80 focus:outline-none">
                  <IoSettingsSharp className="h-3.5 w-3.5" />
                  <IoChevronDown className="h-2.5 w-2.5" />
                </Popover.Button>
                <Popover.Panel className="absolute right-0 z-50 mt-1 w-48 rounded-lg border border-gray-700 bg-palette-black-1 p-2 shadow-lg">
                  <div className="flex flex-col space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Watchlist</span>
                      <Switch.Group as="div" className="flex items-center">
                        <Switch
                          checked={isWatchListOnly}
                          onChange={setIsWatchListOnly}
                          className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                        >
                          <span
                            aria-hidden="true"
                            className={`${isWatchListOnly ? 'bg-yellow-400' : 'bg-gray-600'}
                          pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out
                        `}
                          />
                          <span
                            aria-hidden="true"
                            className={`${isWatchListOnly ? 'translate-x-3' : 'translate-x-0'} ${
                              isWatchListOnly ? 'bg-white' : 'bg-gray-300'
                            } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                          />
                        </Switch>
                        <button
                          type="button"
                          onClick={() => setIsWatchListModalOpen?.(true)}
                          className="ml-1 flex cursor-pointer items-center"
                        >
                          <AiFillStar
                            aria-hidden="true"
                            className={`h-3.5 w-3.5 ${
                              isWatchListOnly ? 'text-yellow-400' : 'text-gray-600'
                            }`}
                          />
                        </button>
                      </Switch.Group>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Earnings</span>
                      <Switch.Group as="div" className="flex items-center">
                        <Switch
                          checked={isEarningsOnly || false}
                          onChange={setIsEarningsOnly || (() => {})}
                          className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                        >
                          <span
                            aria-hidden="true"
                            className={`${isEarningsOnly ? 'bg-graph-purple' : 'bg-gray-600'}
                          pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out
                        `}
                          />
                          <span
                            aria-hidden="true"
                            className={`${isEarningsOnly ? 'translate-x-3' : 'translate-x-0'} ${
                              isEarningsOnly ? 'bg-white' : 'bg-gray-300'
                            } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                          />
                        </Switch>
                        <div
                          className={`ml-0.5 ${
                            isEarningsOnly ? 'text-graph-purple' : 'text-slate-400'
                          }`}
                        >
                          <TbCalendarDollar aria-hidden="true" className="h-4 w-4" />
                        </div>
                      </Switch.Group>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Layout</span>
                      <div className="flex items-center space-x-1">
                        <IconButton
                          type="button"
                          isActive={mobileLayout === MobileLayout.Cards}
                          onClick={() => !!setMobileLayout && setMobileLayout(MobileLayout.Cards)}
                        >
                          <RiLayoutRowLine />
                        </IconButton>
                        <IconButton
                          type="button"
                          isActive={mobileLayout === MobileLayout.Table}
                          onClick={() => !!setMobileLayout && setMobileLayout(MobileLayout.Table)}
                        >
                          <ImTable />
                        </IconButton>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Sound</span>
                      <SpeakerButton
                        className="shadow-sm"
                        isSoundEnabled={isSoundEnabled}
                        setIsSoundEnabled={setIsSoundEnabled}
                      />
                    </div>
                  </div>
                </Popover.Panel>
              </Popover>
            </div>
          </TableHeader>
          <StyledTableBody className="flex h-full flex-col overflow-y-auto overflow-x-hidden">
            {!hasAccess && !isShowcase && (
              <NeedsSubscription hiddenTrades={(totalFlowCount || 0) - trades.length} />
            )}
            {viewType === ViewType.Flow && delayedNoticeWait && (
              <DelayedFlowNotice sessionDate={delayedNoticeWait.sessionDate} />
            )}
            {viewType === ViewType.Flow && !delayedNoticeWait && (
              <AutoSizer>
                {({ width, height }) => (
                  <FixedSizeList
                    height={height}
                    itemCount={processedTrades.length}
                    itemSize={MOBILE_CARD_PIXEL_HEIGHT}
                    width={width}
                  >
                    {RenderCard}
                  </FixedSizeList>
                )}
              </AutoSizer>
            )}
            {viewType === ViewType.Chain && (
              <OptionChainTable
                chainRows={chainRows}
                includeTicker
                hideDate
                isMobile
                expandAllTrigger={expandAllTrigger}
                collapseAllTrigger={collapseAllTrigger}
                onExpandStateChange={handleChainExpandStateChange}
                metricMode={metricMode}
              />
            )}
            {viewType === ViewType.Graph && (
              <FlowOptionGraphView trades={processedTrades} referenceTrades={referenceTrades} />
            )}
          </StyledTableBody>
        </div>
      )}
      <SubscribeModal isOpen={isDownloadModalOpen} onClose={() => setIsDownloadModalOpen(false)} />
      <MoreFeatureModal
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        trade={selectedTrade}
        hasAccess={hasAccess}
        metricMode={metricMode}
        flowSideBasis={flowSideBasis}
      />
    </>
  );
};

export default OptionFlowTable;
